import { toast } from 'sonner';

/**
 * Extracts a human-readable message from an RTK Query / fetch error.
 * The order service uses `{ errors: [{ errorMessage }] }` for validation
 * failures (matches portal's `error?.response?.data?.errors[0]?.errorMessage`).
 * Other endpoints surface a plain `message` string. We fall back to the HTTP
 * status when neither is present so the user gets something better than
 * "Something went wrong".
 */
export function extractMutationErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!error || typeof error !== 'object') return fallback;
  const e = error as { status?: number | string; error?: string; data?: unknown; message?: string };
  const structured = structuredServerMessage(e.data);
  if (structured !== undefined) return structured;
  const data = e.data;
  if (typeof data === 'string' && data.length > 0) return data;
  if (typeof e.message === 'string' && e.message.length > 0) return e.message;
  if (typeof e.error === 'string' && e.error.length > 0) return e.error;
  if (e.status) return `Request failed (status ${e.status}).`;
  return fallback;
}

function structuredServerMessage(raw: unknown): string | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as {
    errors?: Array<{ errorMessage?: string; message?: string }>;
    Errors?: Array<{ ErrorMessage?: string; Message?: string }>;
    Messages?: string[];
    messages?: string[];
    message?: string;
    Message?: string;
    error?: string;
  };
  // routing-service ErrorResult shape: { Messages: string[], Code, ErrorId }
  const routingMessage = (data.Messages ?? data.messages)?.find(
    (m): m is string => typeof m === 'string' && m.length > 0,
  );
  if (routingMessage) return routingMessage;
  // lowercase shape (order service, most REST endpoints)
  const first = data.errors?.[0];
  if (first?.errorMessage) return first.errorMessage;
  if (first?.message) return first.message;
  // capitalized shape (integration / transformation service ValidationErrorResponse)
  const firstCap = data.Errors?.[0];
  if (firstCap?.ErrorMessage) return firstCap.ErrorMessage;
  if (firstCap?.Message) return firstCap.Message;
  if (typeof data.message === 'string' && data.message.length > 0) return data.message;
  if (typeof data.Message === 'string' && data.Message.length > 0) return data.Message;
  if (typeof data.error === 'string') return data.error;
  return undefined;
}

/**
 * The server's own reason when it refuses a request (4xx), else the fallback. A 5xx, or a 4xx
 * whose body is not a structured error (an ingress page, nothing at all), carries nothing the
 * user can act on, so it reads as the plain failure.
 */
export function extractRefusalMessage(error: unknown, fallback: string): string {
  const e = error && typeof error === 'object' ? (error as { status?: unknown; data?: unknown }) : undefined;
  const refused = typeof e?.status === 'number' && e.status >= 400 && e.status < 500;
  return (refused && structuredServerMessage(e?.data)) || fallback;
}

/**
 * Shorthand for the common pattern: `await mutation(args).unwrap().catch(reportMutationError)`.
 * Returns void so it can plug directly into `.catch(reportMutationError)`.
 */
export function reportMutationError(error: unknown): void {
  toast.error(extractMutationErrorMessage(error));
}
